// Global imports
import {
  newTracker,
  newSession,
  setUserId,
  disableAnonymousTracking,
  enableAnonymousTracking,
} from '@snowplow/browser-tracker'
import {
  FormTrackingPlugin,
  enableFormTracking,
} from '@snowplow/browser-plugin-form-tracking'
import {
  ButtonClickTrackingPlugin,
  enableButtonClickTracking,
} from '@snowplow/browser-plugin-button-click-tracking'
import {
  LinkClickTrackingPlugin,
  enableLinkClickTracking,
} from '@snowplow/browser-plugin-link-click-tracking'
import {
  SnowplowEcommercePlugin,
  trackProductView,
} from '@snowplow/browser-plugin-snowplow-ecommerce'

// Types
import type { ProductTrackingObject } from '~/types'

// @REF: https://docs.snowplow.io/docs/sources/trackers/javascript-trackers/web-tracker/tracker-setup/initialization-options/

// This function disables anonymous tracking
// When the user has given consent for tracking
// It is called via the GTM tag -> Override datalayer.
const windowSnowplowDisable = () => {
  if (typeof window !== 'undefined') {
    window.snowplow_disable_anonymous_tracking = () => {
      disableAnonymousTracking()
    }
  }
}

// This function enables anonymous tracking
// When the user has removed consent for tracking
// It is called via the GTM tag -> Override datalayer.
const windowSnowplowEnable = () => {
  if (typeof window !== 'undefined') {
    window.snowplow_enable_anonymous_tracking = () => {
      enableAnonymousTracking()
    }
  }
}

// Create the tracker
export const snowplowInitClient = (
  appId: string,
  collectorUrl: string,
  debug?: string
) => {
  // Plugins to initialise
  const plugins = [
    FormTrackingPlugin(),
    ButtonClickTrackingPlugin(),
    LinkClickTrackingPlugin(),
    SnowplowEcommercePlugin(),
  ]

  newTracker(
    'client-side-tracker',
    debug == 'true' ? 'http://localhost:9090' : collectorUrl,
    {
      appId: appId,
      discoverRootDomain: true,
      cookieSameSite: 'Lax',
      eventMethod: 'post',
      contexts: {
        webPage: true,
        session: true,
        browser: true,
      },
      plugins: plugins,
      anonymousTracking: false,
    }
  )

  // Snowplow functions
  enableFormTracking()
  enableButtonClickTracking()
  enableLinkClickTracking()

  // Custom consent functions
  windowSnowplowDisable()
  windowSnowplowEnable()
}

/*
 * Function to extract the Snowplow Domain User Information from the first-party cookie set by the Snowplow JavaScript Tracker
 *
 * @param string cookieName (optional) The value used for "cookieName" in the tracker constructor argmap
 * (leave blank if you did not set a custom cookie name)
 *
 * @return string or bool The ID string if the cookie exists or false if the cookie has not been set yet
 */
type SnowplowCookieIds = {
  domainUserid: string | null
  domainSessionidx: string | null
  domainSessionid: string | null
}

const safeDecodeURIComponent = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export const getSnowplowDuid = (
  cookieHeader: string
): SnowplowCookieIds | null => {
  // Match `_sp_id.<suffix>=<value>` up to the next `;`
  const match = cookieHeader.match(/(?:^|;\s*)_sp_id\.[^=]+=([^;]+)/)

  if (!match?.[1]) {
    console.debug('[snowplow] no _sp_id cookie found')
    return null
  }

  const raw = match[1]
  const value = safeDecodeURIComponent(raw)

  // Expected: duid.createTs.visitCount.currentVisitTs.lastVisitTs.sessionId
  const split = value.split('.')

  if (split.length < 6) {
    console.debug('[snowplow] malformed _sp_id cookie', { value })
    return null
  }

  return {
    domainUserid: split[0] ?? null,
    domainSessionidx: split[2] ?? null,
    domainSessionid: split[5] ?? null,
  }
}

export const snowplowTrackProductView = ({
  stockCode,
  name,
  guidePrice,
  salePrice,
  category,
}: ProductTrackingObject) => {
  // Check we have the required data
  const priceToUse =
    salePrice?.amount && salePrice?.currency ? salePrice : guidePrice

  // Snowplow page view
  const options = {
    id: stockCode,
    name: name,
    price: priceToUse.amount,
    currency: priceToUse.currency,
    category: category ?? '',
  }

  trackProductView(options)
}

/**
 * Applies user identity changes to Snowplow.
 * - logout => clears userId + starts new session
 * - login/switch => sets userId (+ new session if switching from another user)
 * - anonymous => clears userId
 */
export type SnowplowUserLike = {
  id?: string | number | null
}

export const applySnowplowUserId = (
  userId: string | null | undefined,
  prevUserId: string | null
): { nextUserId: string | null } => {
  // Auth not resolved yet: DO NOTHING (don’t clear!)
  if (userId === undefined) {
    return { nextUserId: prevUserId }
  }

  // Resolved logged out
  if (prevUserId && userId === null) {
    setUserId(null)
    newSession()
    return { nextUserId: null }
  }

  // Resolved logged in or switched
  if (userId && userId !== prevUserId) {
    setUserId(userId)
    if (prevUserId) newSession()
    return { nextUserId: userId }
  }

  // Resolved still logged out
  if (userId === null) {
    setUserId(null)
    return { nextUserId: null }
  }

  // Unchanged logged in
  return { nextUserId: prevUserId }
}
